// ============================================================================
// FILE: src/firebase.ts  (HARDENED — prevents emulator leakage in production)
// ============================================================================

import { initializeApp } from 'firebase/app';
import {  
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  onAuthStateChanged, 
  User, 
  EmailAuthProvider, 
  reauthenticateWithCredential, 
  updateEmail, 
  updatePassword,
  deleteUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  connectAuthEmulator
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  collection, 
  onSnapshot, 
  setDoc, 
  getDoc, 
  getDocFromServer, 
  getDocs, 
  query, 
  orderBy, 
  limit, 
  addDoc, 
  deleteDoc, 
  updateDoc, 
  serverTimestamp, 
  connectFirestoreEmulator 
} from 'firebase/firestore';
import firebaseConfig from './firebase-applet-config.json';

// Initialize Firebase SDK
const app = initializeApp(firebaseConfig);

// Support named firestore database ID (required in AI Studio / production) while maintaining emulator compatibility
const databaseId = (firebaseConfig as any).firestoreDatabaseId;
export const db = databaseId ? getFirestore(app, databaseId) : getFirestore(app);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// EMULATOR GUARD — PRODUCTION HARDENED
// Defensive triple-check so we NEVER wire emulators in a production build,
// even if import.meta.env.DEV is accidentally true or the build is misconfigured.
const isViteDev = import.meta.env?.DEV === true;
const isBrowser = typeof window !== 'undefined';
const isLocalhost = isBrowser && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
);
const useEmulators = isViteDev && isLocalhost;

if (useEmulators) {
  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectAuthEmulator(auth, 'http://localhost:9099');
    console.log('[Firebase] Connected to local emulators (Firestore: 8080, Auth: 9099)');
  } catch (e) {
    console.warn('[Firebase] Emulator connection failed:', e);
  }
} else {
  console.log('[Firebase] Production mode — live Firestore & Auth services');
}

// Operation Types for Error Handling
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

// Error Handling Interface
export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

// Centralized Error Handler
export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export { 
  signInWithPopup, 
  onAuthStateChanged, 
  doc, 
  collection, 
  onSnapshot, 
  setDoc, 
  getDoc, 
  getDocs,
  getDocFromServer,
  query, 
  orderBy, 
  limit, 
  addDoc, 
  deleteDoc, 
  updateDoc,
  serverTimestamp,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  updatePassword,
  deleteUser,
  getFirestore
};
export type { User };
